import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { AlertOctagon, Activity } from 'lucide-react';

// Polls the lightweight Deployment Regression endpoint every 5 minutes.
// When the most-recent marked release shows MORE unhealthy integrations than
// the previous marked release, this renders a flashing red bell that links to
// the Deployment Status sub-tab. Catches "deployed and forgot to set MSG91 key"
// mistakes within minutes instead of waiting for a user to report a broken OTP.
//
// Admin / Business Head only.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

export const DeploymentRegressionAlert = () => {
  const { currentUser, api } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const isAdmin = currentUser && ['admin', 'business_head'].includes(currentUser.role);

  const fetchAlert = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.getDeploymentRegression();
      setData(res.data);
    } catch {
      /* silent — alert is best-effort */
    } finally {
      setLoaded(true);
    }
  }, [api, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    fetchAlert();
    const id = setInterval(fetchAlert, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isAdmin, fetchAlert]);

  // Hide entirely until we know the answer to avoid a flicker.
  if (!isAdmin || !loaded || !data?.ready || !data?.active) return null;

  const regressed = data?.regression?.newly_unhealthy || [];
  const fromTag = data?.regression?.from?.release_tag || '—';
  const toTag = data?.regression?.to?.release_tag || data?.release_tag || '—';
  const tooltip = `Integration regressed in ${toTag} vs ${fromTag}: ${regressed.join(', ')}\nClick to open Deployment Status`;

  return (
    <button
      type="button"
      data-testid="deployment-regression-alert"
      title={tooltip}
      onClick={() => navigate('/admin#deployment')}
      className="relative p-2 rounded-lg hover:bg-red-50 transition-colors"
    >
      <AlertOctagon className="w-5 h-5 text-red-600 animate-pulse" strokeWidth={1.8} />
      <span
        data-testid="deployment-regression-badge"
        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white"
      >
        {regressed.length}
      </span>
    </button>
  );
};
